import styled from 'styled-components';
import {TscStyles, TscThemeName} from '@techsmith/tsc-cloud-style-guide';
import CssUtil from '../../util/CssUtil';
import CssConstants from '../../constants/cssConstants';
import React, {memo} from 'react';
import {useTscTheme} from '../../context/themeProvider';

interface IReactionButtonTheme {
   color: string;
   borderColor: string;
   background: string;
   hoverBackground: string;
   activeBackground: string;
   activeBoxShadow: string;
}

const buttonThemes = {
   [TscThemeName.dawn]: {
      normal: {
         color: TscStyles.color.text.mediumDark,
         borderColor: TscStyles.color.ui.dawn.mediumDark,
         background: TscStyles.color.ui.dawn.light,
         hoverBackground: CssUtil.toRgba(TscStyles.color.ui.dawn.medium, CssConstants.reactionsButtonHoverOpacity),
         activeBackground: CssUtil.toRgba(TscStyles.color.ui.dawn.medium, CssConstants.reactionsButtonHoverOpacity),
         activeBoxShadow: 'box-shadow: inset 0 2px 4px rgba(0, 0, 20, 0.1)'
      } as IReactionButtonTheme,
      embedded: {
         color: TscStyles.color.text.mediumDark,
         borderColor: TscStyles.color.ui.dawn.mediumDark,
         background: 'none',
         hoverBackground: CssUtil.toRgba(TscStyles.color.ui.dawn.light, CssConstants.reactionsButtonEmbedHoverOpacity),
         activeBackground: CssUtil.toRgba(TscStyles.color.ui.dawn.medium, CssConstants.reactionsButtonHoverOpacity),
         activeBoxShadow: 'box-shadow: none'
      } as IReactionButtonTheme
   },
   [TscThemeName.dusk]: {
      normal: {
         color: TscStyles.color.text.light,
         borderColor: TscStyles.color.ui.dusk.medium,
         background: TscStyles.color.ui.dusk.mediumDark,
         hoverBackground: TscStyles.color.action.secondaryDusk,
         activeBackground: TscStyles.color.action.secondaryDusk,
         activeBoxShadow: 'box-shadow: inset 0 2px 4px rgba(0, 0, 20, 0.4)'
      } as IReactionButtonTheme,
      embedded: {
         color: TscStyles.color.text.light,
         borderColor: TscStyles.color.ui.dusk.medium,
         background: 'none',
         hoverBackground: TscStyles.color.action.secondaryDusk,
         activeBackground: TscStyles.color.action.secondaryDusk,
         activeBoxShadow: 'box-shadow: none'
      } as IReactionButtonTheme
   }
};

const ReactionMenuButton = styled.button<{
   isActive: boolean;
   isDisabled: boolean;
   width: number;
   height: number;
   fontSize: number;
   padding: string;
   margin: string;
   borderRadius: string;
   hasBorder: boolean;
   buttonTheme: IReactionButtonTheme;
}>`
   position: relative;
   display: flex;
   flex-direction: row;
   align-items: center;
   ${props => props.width && `width: ${props.width}rem`};
   ${props => props.height && `height: ${props.height}rem`};
   padding: ${props => props.padding ? props.padding : 0};
   margin: ${props => props.margin ? props.margin : 0};
   border-radius: ${props => props.borderRadius ? props.borderRadius : 0};
   border: ${props => props.hasBorder ? `1px solid ${props.buttonTheme.borderColor}` : 0};
   background: ${props => props.isActive ? props.buttonTheme.activeBackground : props.buttonTheme.background};
   ${props => props.isActive && props.buttonTheme.activeBoxShadow};
   font-family: Figtree, sans-serif;
   font-size: ${props => `${props.fontSize}rem`};
   font-weight: 600;
   color: ${props => props.buttonTheme.color};
   fill: ${props => props.buttonTheme.color};
   cursor: pointer;
   pointer-events: ${props => props.isDisabled ? 'none' : 'auto'};

   &:hover {
      background: ${props => props.buttonTheme.hoverBackground};
   }

   &::-moz-focus-inner {
      border: 0;
   }

   &:focus-visible {
      outline: none;

      &:after{
         content: '';
         display: block;
         position: absolute;
         top: 1px;
         right: 1px;
         bottom: 1px;
         left: 1px;
         border: 2px solid currentColor;
         border-radius: ${TscStyles.border.radius.md};
      }
   }

   &:active {
      background: ${props => props.buttonTheme.activeBackground};
      ${props => props.buttonTheme.activeBoxShadow};
   }
`;

export const ReactionUIButtonBase: React.FC<IReactionUIButtonProps> = ({
   children,
   isEmbedded,
   isActive,
   isDisabled,
   width,
   height,
   fontSize,
   padding,
   margin,
   borderRadius,
   hasBorder,
   title,
   ariaLabel,
   className,
   onClick
}) => {
   const theme = useTscTheme();

   return (
      <ReactionMenuButton
         title={title}
         aria-label={ariaLabel}
         className={className}
         onClick={onClick}
         isActive={isActive}
         isDisabled={isDisabled}
         width={width}
         height={height}
         fontSize={fontSize}
         padding={padding}
         margin={margin}
         borderRadius={borderRadius}
         hasBorder={hasBorder}
         buttonTheme={buttonThemes[theme][isEmbedded ? 'embedded' : 'normal']}
      >
         {children}
      </ReactionMenuButton>
   );
};

export interface IReactionUIButtonProps {
   children?: React.ReactNode;
   isEmbedded: boolean;
   isActive: boolean;
   isDisabled?: boolean;
   width?: number;
   height?: number;
   fontSize: number;
   padding?: string;
   margin?: string;
   borderRadius?: string;
   hasBorder: boolean;
   title: string;
   ariaLabel?: string;
   className?: string;
   onClick: () => void;
}

export default memo(ReactionUIButtonBase);
